import type { FunctionComponent } from "react";
import { useCallback, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import _ from "lodash";
import queryString from "query-string";
import styled from "styled-components";

import { IconButton } from "@similarweb/ui-components";

import { chosenSites } from "app/common/services/chosenSites";
import { swSettings } from "app/common/services/swSettings";
import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import {
    AdsenseCell,
    CategoryFilterCell,
    ChangePercentage,
    IndexCell,
    RankCell,
    TrafficShare,
    WebsiteTooltipTopCell,
} from "app/components/React/Table/cells";
import {
    DefaultCellHeader,
    DefaultCellHeaderRightAlign,
    HeaderCellBlank,
} from "app/components/React/Table/headerCells";
import SWReactTableWrapper from "app/components/React/Table/SWReactTableWrapper";
import { i18nFilter, translate } from "app/filters/i18nFilter";
import { i18nCategoryFilter, subCategoryFilter } from "app/filters/ngFilters";
import { OutgoingTrafficEnriched } from "app/pages/website-analysis/outgoing-traffic/OutgoingTrafficEnriched";
import { OutgoingTrafficPageTableTop } from "app/pages/website-analysis/outgoing-traffic/OutgoingTrafficPageTableTop";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { dateForTooltip } from "app/services/DurationService";
import DurationService from "app/services/DurationService";
import { allTrackers } from "app/services/track/track";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

const NewChangeWrapper = styled(FlexRow)`
    justify-content: flex-end;
`;

declare const similarweb;

const PAGE_SIZE = 100;

const OutgoingTrafficPageInner: FunctionComponent<any> = (props) => {
    const i18n = i18nFilter();
    const subCategory = subCategoryFilter();
    const isCompare = chosenSites.isCompare();
    const selectedSites = chosenSites.map((item, infoItem) => {
        return { value: item, label: infoItem.displayName, icon: infoItem.icon };
    });
    const [tableTopData, setTableTopData] = useState<any>({});
    const [pageNumber, setPageNumber] = useState(0);
    const changePageCallback = (page) => setPageNumber(page);
    const domains = chosenSites.sitelistForLegend();

    const {
        params: {
            key,
            country,
            isWWW,
            duration,
            webSource,
            outagoing_orderby, // Yes, I know there is a typo here. Legacy links.
            outagoing_filters,
            outagoing_page,
            selectedSite: selectedSiteProps,
        },
        state,
    } = useCurrentStateAndParams();
    const selectedSiteExists = selectedSiteProps ? selectedSites.find((s) => s.value === selectedSiteProps) : null;
    const selectedSite = selectedSiteExists ? selectedSiteExists.value : selectedSites[0].value;
    const filtersObject: any = {
        domain: {
            action: "contains",
        },
        category: {
            action: "category",
        },
    };
    const availableFilters: any = {};
    if (outagoing_filters) {
        outagoing_filters.split("+").forEach((filter) => {
            const values = filter.split(";");
            availableFilters[values[0]] = { ...filtersObject[values[0]], value: values[2] };
        });
    }
    // Convert filters to API params filter string.
    const filter = Object.keys(availableFilters)
        .map((filter) => {
            const { action, value } = availableFilters[filter];
            return [filter, action, `"${value}"`].join(";");
        })
        .join(",");
    const createFiltersForUrl = (filters) =>
        Object.keys(filters)
            .map((filter) => {
                const { action, value } = filters[filter];
                return [filter, action, value].join(";");
            })
            .join("+");
    let field = "Share";
    let sortDirection = "desc";
    if (outagoing_orderby) {
        [field, sortDirection] = outagoing_orderby.split(" ");
    }
    const { from, to, isWindow } = DurationService.getDurationData(duration).forAPI;
    const params = {
        filter,
        country,
        from,
        isWWW: isWWW !== "*",
        isWindow,
        key: isCompare ? selectedSite : key,
        orderby: `${field} ${sortDirection}`,
        sort: field,
        asc: sortDirection === "asc",
        page: outagoing_page,
        to,
        webSource,
    } as any;
    const durationTooltip = _.mapValues(
        DurationService.getDurationData(duration || "", "", "").forTooltip as dateForTooltip,
        (v) => {
            return decodeURIComponent(v);
        },
    );
    const durationTooltipParams = {
        currentMonth: durationTooltip.to,
        lastMonth: durationTooltip.from,
    };
    const columns = [
        {
            fixed: true,
            cellComponent: ({ row }) => {
                return (
                    <>
                        {!row.parent ? (
                            <IconButton
                                iconName="chev-right"
                                type="flat"
                                className="enrich"
                            />
                        ) : null}
                    </>
                );
            },
            sortable: false,
            headerComponent: HeaderCellBlank,
            isResizable: false,
            width: 40,
            columnClass: "collapseControlColumn",
            cellClass: "collapseControlCell",
            disableHeaderCellHover: true,
        },
        {
            fixed: true,
            cellComponent: IndexCell,
            disableHeaderCellHover: true,
            sortable: false,
            width: 46,
            isResizable: false,
        },
        {
            minWidth: 204,
            isResizable: true,
            sortable: true,
            field: "Domain",
            showTotalCount: true,
            displayName: i18n("analysis.dest.out.table.columns.domain.title"),
            tooltip: i18n("analysis.dest.out.table.columns.domain.title.tooltip"),
            cellComponent: WebsiteTooltipTopCell,
        },
        {
            minWidth: 226,
            isResizable: true,
            sortable: true,
            field: "Category",
            displayName: i18n("analysis.dest.out.table.columns.category.title"),
            tooltip: i18n("analysis.dest.out.table.columns.category.title.tooltip"),
            cellComponent: (row) => {
                const onItemClick = () => onSelectCategory(row.value);
                return (
                    <CategoryFilterCell
                        {...row}
                        onItemClick={onItemClick}
                    />
                );
            },
        },
        {
            minWidth: 96,
            isResizable: true,
            sortable: true,
            field: "Rank",
            displayName: i18n("analysis.dest.out.table.columns.rank.title"),
            tooltip: i18n("analysis.dest.out.table.columns.rank.title.tooltip"),
            cellComponent: RankCell,
            headerComponent: DefaultCellHeaderRightAlign,
            format: "rank",
        },
        {
            minWidth: 112,
            sortable: true,
            isResizable: true,
            field: "Share",
            displayName: i18n("analysis.dest.out.table.columns.share.title"),
            tooltip: i18n("analysis.dest.out.table.columns.share.title.tooltip"),
            cellComponent: TrafficShare,
        },
        {
            minWidth: 104,
            isResizable: true,
            sortable: true,
            field: "Change",
            displayName: i18n("analysis.dest.out.table.columns.change.title"),
            tooltip: i18n(
                "analysis.dest.out.table.columns.change.title.tooltip",
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                durationTooltipParams,
            ),
            cellComponent: (props: any) =>
                props.row.NewChange ? (
                    <NewChangeWrapper>{i18n("new.label.pill")}</NewChangeWrapper>
                ) : (
                    <ChangePercentage {...props} />
                ),
            headerComponent: DefaultCellHeaderRightAlign,
        },
        {
            width: 72,
            sortable: true,
            field: "HasAdsense",
            displayName: i18n("analysis.all.table.columns.googleAds.title"),
            tooltip: i18n("analysis.all.table.columns.googleAds.title.tooltip"),
            cellComponent: AdsenseCell,
        },
    ].map((col: any) => {
        const isSorted = col.field === field;
        return {
            ...col,
            visible: col.visible !== false,
            headerComponent: col.headerComponent || DefaultCellHeader,
            isSorted,
            sortDirection,
            isResizable: col.isResizable !== false,
        };
    });
    const onSort = ({ field, sortDirection }) => {
        proNavigator.applyUpdateParams({
            outagoing_orderby: `${field} ${sortDirection}`,
        });
    };
    const getSelectedSite = () => {
        const sites = chosenSites.sitelistForLegend();
        const { selectedSite } = proNavigator.getParams();
        const selectedSiteItem = sites.find((site) => site.name === selectedSite);
        if (selectedSite && selectedSiteItem) {
            return selectedSiteItem;
        } else {
            return sites[0];
        }
    };
    const getCategoryLink = (newCategory) => {
        const category = { ...filtersObject.category };
        category.value = newCategory;
        const filters = { ...availableFilters, category };
        return decodeURIComponent(
            proNavigator.href(proNavigator.current().name, {
                outagoing_filters: createFiltersForUrl(filters),
            }),
        );
    };
    const getDataCallback = (data) => {
        const { TotalVisits, TotalCount, TotalShare, AllCategories, Categories, Topics } = data;
        const domainName = getSelectedSite().name;
        setTableTopData({
            AllCategories,
            TotalVisits,
            TotalUnGroupedCount: TotalCount,
            Topics,
            TotalShare: TotalShare > 0.99 ? 1 : TotalShare || 0,
            categoriesData: similarweb.utils
                .formatTopList(Categories[domainName], 6, {
                    transformFunction: similarweb.utils.addCategoryIdFromName,
                })
                .map((cat) => {
                    return {
                        category: i18nCategoryFilter()(cat.Name),
                        categoryApi: cat.Id,
                        ...Object.entries(Categories[domainName]).reduce((acc, [domain, domainCategories]) => {
                            return {
                                ...acc,
                                [domainName]: cat.Value,
                            };
                        }, {}),
                    };
                }),
            domainMetaData: [
                {
                    icon: chosenSites.listInfo[domainName].icon,
                    name: domainName,
                    color: chosenSites.getSiteColor(domainName),
                },
            ],
        });
    };
    const downloadExcelPermitted = swSettings.current.resources.IsExcelAllowed;
    const getExcelUrl = () => {
        const queryStringParams = queryString.stringify(params);
        return `/export/analysis/GetOutgoingTsv?${queryStringParams}`;
    };

    const isWebsiteAnalysis = state?.name === "digitalsuite_website_referrals_outgoingtraffic";
    const metricName = isWebsiteAnalysis ? "WebsiteAnalysisOutgoingReferrals" : "OutgoingReferrals";
    const a2d = useCallback(() => {
        return (
            <AddToDashboard
                metric={metricName}
                modelType="fromWebsite"
                type="OutgoingReferralsDashboardTable"
                webSource={webSource}
                filters={{
                    filter,
                }}
            />
        );
    }, [webSource, filter]);
    const onSearchTermFilterChange = (value) => {
        let filters;
        if (value === "") {
            const { domain, ...restFilters } = availableFilters;
            filters = restFilters;
        } else {
            const domain = { ...filtersObject.domain };
            domain.value = value;
            filters = { ...availableFilters, domain };
        }
        proNavigator.applyUpdateParams({
            outagoing_filters: createFiltersForUrl(filters),
        });
    };
    const onSelectCategory = (value) => {
        let filters;
        if (!value) {
            const { category, ...restFilters } = availableFilters;
            filters = restFilters;
        } else {
            const category = { ...filtersObject.category };
            category.value = value;
            filters = { ...availableFilters, category };
        }
        proNavigator.applyUpdateParams({
            outagoing_filters: createFiltersForUrl(filters),
        });
    };
    const onClearAll = () => {
        proNavigator.applyUpdateParams({
            outagoing_filters: null,
        });
    };
    const onSelectSite = (selectedSite, numberOfResults) => {
        allTrackers.dispatchTrackEvent("Tab", "click", `Filter Domain/${selectedSite || ""}/${numberOfResults || ""}`);
        proNavigator.go(
            proNavigator.current().name,
            Object.assign(proNavigator.getParams(), { selectedSite: selectedSite }),
        );
    };

    const onClearSite = () => {
        proNavigator.go(proNavigator.current().name, Object.assign(proNavigator.getParams(), { selectedSite: null }));
    };

    const transformData = (data) => {
        return {
            ...data,
            Records: data.Records.map((record) => {
                return {
                    ...record,
                    TotalSharePerMonth: record.TotalSharePerMonth ? record.TotalSharePerMonth : [],
                    url: proNavigator.href("digitalsuite_website_websiteperformance", {
                        ...proNavigator.getParams(),
                        key: record.Domain,
                    }),
                };
            }),
        };
    };
    return (
        <SWReactTableWrapper
            changePageCallback={changePageCallback}
            tableOptions={{
                metric: "OutgoingTrafficTable",
                EnrichedRowComponent: (enrichedProps) => {
                    if (typeof enrichedProps?.row === "undefined") {
                        return null;
                    }
                    return (
                        <OutgoingTrafficEnriched
                            {...enrichedProps}
                            pageSize={PAGE_SIZE}
                            pageNumber={pageNumber}
                        />
                    );
                },
                get enrichedRowComponentAppendTo() {
                    return ".outgoing-traffic-table";
                },
                get enrichedRowComponentHeight() {
                    return 580;
                },
                shouldApplyEnrichedRowHeightToCell: false,
                shouldEnrichRow: (_props, _index, e) => {
                    const openEnrich = e?.currentTarget?.childNodes?.[0]?.className.includes("enrich");
                    if (openEnrich) {
                        allTrackers.trackEvent("Open", "Click", "Traffic Over Time/Expand");
                    }
                    return openEnrich;
                },
                customTableClass: "outgoing-traffic-table",
            }}
            serverApi={`/api/websiteanalysis/GetOutgoingTable`}
            tableColumns={columns}
            initialFilters={params}
            recordsField="Records"
            totalRecordsField="TotalCount"
            onSort={onSort}
            preventDefaultOnSort
            getDataCallback={getDataCallback}
            transformData={transformData}
            pageIndent={1}
        >
            {(topComponentProps) => (
                <OutgoingTrafficPageTableTop
                    selectedCategory={
                        availableFilters.category
                            ? decodeURIComponent(subCategory(availableFilters.category.value))
                            : null
                    }
                    selectedCategoryId={availableFilters.category?.value}
                    onSelectCategory={onSelectCategory}
                    downloadExcelPermitted={downloadExcelPermitted}
                    excelLink={getExcelUrl()}
                    tableTopData={tableTopData}
                    a2d={a2d}
                    getCategoryLink={getCategoryLink}
                    searchValue={availableFilters.domain?.value}
                    onChange={onSearchTermFilterChange}
                    onClearAll={onClearAll}
                    selectedSite={selectedSite}
                    selectedSites={selectedSites}
                    isCompare={isCompare}
                    onSelectSite={onSelectSite}
                    onClearSite={onClearSite}
                    {...topComponentProps}
                />
            )}
        </SWReactTableWrapper>
    );
};
export const OutgoingTrafficPage = OutgoingTrafficPageInner;
